/**
 * Assembles a properly-formatted US address from its component parts.
 *
 * Missing/blank parts are skipped, so partial addresses degrade gracefully
 * (e.g. a record with only city + state still renders cleanly). The output
 * follows USPS ordering: street line(s), then "City, ST ZIP".
 *
 * @example
 * formatAddress({ street: "123 Main St", unit: "Apt 4", city: "Austin", state: "TX", zip: "78701" })
 * // => "123 Main St, Apt 4, Austin, TX 78701"
 */
export type AddressParts = {
  street?: string | null;
  /** Secondary line: apartment, suite, unit, etc. */
  unit?: string | null;
  city?: string | null;
  /** Two-letter state code or full state name. */
  state?: string | null;
  zip?: string | null;
};

const clean = (value?: string | null): string => (value ?? "").trim();

export const formatAddress = (parts: AddressParts): string => {
  const street = clean(parts.street);
  const unit = clean(parts.unit);
  const city = clean(parts.city);
  const state = clean(parts.state);
  const zip = clean(parts.zip);

  const streetLine = [street, unit].filter(Boolean).join(", ");

  // "City, ST ZIP" — comma only when a city precedes the state/zip.
  const stateZip = [state, zip].filter(Boolean).join(" ");
  const cityLine = [city, stateZip].filter(Boolean).join(city ? ", " : "");

  return [streetLine, cityLine].filter(Boolean).join(", ");
};
